import { Dispatch, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import classNames from 'classnames';
import { Action } from 'redux';

import {
  CallStatus,
  CryptocurrenciesEnum,
  CryptocurrenciesNetworkCodesEnum,
} from '../../../lib/enums';
import lang from '../../../lib/language';
import { RootState } from '../../../lib/store';
import {
  getIndividualLinkedAccounts,
  registerCryptoLinkedAccount,
  setRegisterCryptoLinkedAccountStatusToInitial,
} from '../../../lib/store/payment';
import { PaymentCall, RegisterCryptoLinkedAccountPayload } from '../../../lib/store/payment/types';
import { isValidSolanaAddress } from '../../../lib/util/CryptoHelpers';
import { ControlError } from '../../../lib/util/ValidationSchemes/ValidationTypes';

import { useBrowserWidth, useIsMobile } from '../../../hooks';
import Button from '../../../shared_components/Button/Button';
import { UI_ROUTES } from '../../../util/routes';
import { BaseModal, InputControl, LogoAndCloseButton, Preloader } from '../..';
import ContainerBackButton from '../../ContainerBackButton/ContainerBackButton';
import GoBackButton from '../../GoBackButton/GoBackButton';
import AddBankAccountModal from '../../PayoutsModals/AddBankAccountModal';

import AddCryptoAddressScreen from './AddCryptoAddressScreen';

import styles from './AddCryptoAddress.module.scss';

type Props = {} & ReduxProps;

const AddCryptoAddress = (props: Props) => {
  const {
    requestRegisterCryptoLinkedAccount,
    submitRegisterCryptoLinkedAccount,
    resetCryptoLinkedAccountStatus,
    refreshLinkedAccounts,
  } = props;

  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useIsMobile();
  const { browserWidthSize } = useBrowserWidth();

  const [cryptoWalletAddress, setCryptoWalletAddress] = useState<string>('');
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [hasRequest, setHasRequest] = useState<boolean>(false);
  const [isInitialised, setIsInitialised] = useState<boolean>(false);

  const isDefaultGoBackButton = browserWidthSize >= 989 && !isMobile;

  useEffect(() => {
    if (cryptoWalletAddress === '') setIsSubmitted(false);
  }, [cryptoWalletAddress]);

  useEffect(() => {
    if (!isInitialised) {
      resetCryptoLinkedAccountStatus();
      setIsInitialised(true);
    }
  }, [isInitialised, resetCryptoLinkedAccountStatus]);

  useEffect(() => {
    if (!hasRequest) return;

    if (requestRegisterCryptoLinkedAccount?.status === CallStatus.ERROR) {
      navigate(
        {
          pathname: UI_ROUTES.error,
        },
        {
          state: {
            errorCodeAddCryptoAddress: requestRegisterCryptoLinkedAccount.errorCode,
            target: location.pathname,
          },
        },
      );
    } else if (requestRegisterCryptoLinkedAccount?.status === CallStatus.READY) {
      setHasRequest(false);
      refreshLinkedAccounts();
    }
  }, [hasRequest, requestRegisterCryptoLinkedAccount]);

  const handleBack = () => {
    navigate(UI_ROUTES.payouts + UI_ROUTES.payoutsMethods);
  };

  const handleClose = () => {
    navigate(UI_ROUTES.payouts);
  };

  const handleContinue = () => {
    navigate(UI_ROUTES.payouts + UI_ROUTES.withdrawCryptoAmount);
  };

  const handleConfirm = () => {
    setIsSubmitted(true);
    if (!isValidSolanaAddress(cryptoWalletAddress)) return;
    setIsSubmitted(false);

    const payload: RegisterCryptoLinkedAccountPayload = {
      cryptoWalletAddress,
      cryptoCode: CryptocurrenciesEnum.USDT,
      cryptoNetworkCode: CryptocurrenciesNetworkCodesEnum.SOLANA,
    };
    submitRegisterCryptoLinkedAccount(payload);
    setHasRequest(true);
  };

  const isConfirmDisabled = cryptoWalletAddress.trim() === ''
    || requestRegisterCryptoLinkedAccount?.status === CallStatus.PENDING;

  const addressErrors: ControlError[] = isSubmitted && !isValidSolanaAddress(cryptoWalletAddress)
    ? [{ id: 0, error: lang.payoutsAddCryptoInvalidAddress() }]
    : [];

  return (
    <>
      {hasRequest && <Preloader owner="Add crypto address" isFullScreen stylePreset="customOne" />}
      <div className={styles.wrapperContainer}>
        <LogoAndCloseButton
          displayCloseButton={!isMobile}
          displayBackButton={isDefaultGoBackButton}
          onBackBtnPress={handleBack}
          onClose={handleClose}
          newWebGoBackButtonDesign
        />
        {!isDefaultGoBackButton && !isMobile && (
          <div className={styles.goBackButtonContainer}>
            <ContainerBackButton onBackBtnPress={handleBack} />
          </div>
        )}
        {isMobile && (
          <div className={styles.mobileBackContainer}>
            <GoBackButton onBackBtnPress={handleBack} />
          </div>
        )}
        <div className={styles.containerContent}>
          <div
            className={classNames(styles.wrapperContent, {
              [styles.mobileWrapperContent]: isMobile,
            })}
          >
            <div className={styles.title}>{lang.payoutsAddCryptoTitle()}</div>
            <div className={styles.description}>{lang.payoutsAddCryptoDescription()}</div>
            <div className={styles.inputLabel}>{lang.payoutsAddCryptoInputLabel()}</div>
            <InputControl
              id="cryptoWalletAddress"
              name=""
              extraStyles
              placeholder={lang.payoutsAddCryptoInputPlaceholder()}
              value={cryptoWalletAddress}
              onValueChange={(value: string | number) => setCryptoWalletAddress(value as string)}
              errors={addressErrors}
            />
            <Button
              disabled={isConfirmDisabled}
              className={styles.confirmBtn}
              fullWidth
              biggerFontSize
              onClick={handleConfirm}
            >
              {lang.payoutsAddCryptoConfirmBtn()}
            </Button>
            <div className={styles.footnote}>{lang.payoutsAddCryptoFootnote()}</div>
          </div>
        </div>
      </div>
      <BaseModal
        isOpen={
          !isMobile
          && !!requestRegisterCryptoLinkedAccount
          && requestRegisterCryptoLinkedAccount.status === CallStatus.READY
        }
      >
        <AddBankAccountModal
          handleContinue={handleContinue}
          textInfoModal={{
            title: lang.payoutsAddCryptoSuccessTitle(),
            description1: lang.payoutsAddCryptoSuccessDescription1(),
            description2: lang.payoutsAddCryptoSuccessDescription2(),
          }}
        />
      </BaseModal>
      <AddCryptoAddressScreen
        isOpen={
          isMobile
          && !!requestRegisterCryptoLinkedAccount
          && requestRegisterCryptoLinkedAccount.status === CallStatus.READY
        }
        onContinue={handleContinue}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  requestRegisterCryptoLinkedAccount:
    state.payment.statusByCall[PaymentCall.registerCryptoLinkedAccount],
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  submitRegisterCryptoLinkedAccount: (payload: RegisterCryptoLinkedAccountPayload) =>
    dispatch(registerCryptoLinkedAccount(payload)),
  resetCryptoLinkedAccountStatus: () => dispatch(setRegisterCryptoLinkedAccountStatusToInitial()),
  refreshLinkedAccounts: () => dispatch(getIndividualLinkedAccounts()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(AddCryptoAddress);
